// src/pages/Competition/EditCompetitionSettingsModal.jsx
import React, { useEffect, useState } from "react";
import { FaSave, FaTimes } from "react-icons/fa";
import Button from "./../../components/Button/Button";
import {
  getCompetitionSettingsByCompetitionId,
  updateCompetitionSetting,
} from "../../services/competitionsettingsServices";
import { toast } from "react-toastify";

/* SlideOver aligné au style de création/édition */
const SlideOver = ({ open, onClose, title, children }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose?.();
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div className="absolute inset-0 bg-black/50" />
      <div
        className="relative ml-auto h-full w-full max-w-2xl bg-white dark:bg-slate-900 shadow-xl
                   border-l border-gray-200 dark:border-slate-700 p-5 sm:p-6
                   transform transition translate-x-0 animate-[mmSlideIn_220ms_ease-out]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {title}
          </h3>
          <button
            onClick={onClose}
            aria-label="Fermer"
            className="h-8 w-8 rounded-full bg-black/10 dark:bg-white/10 hover:opacity-90 flex items-center justify-center text-gray-700 dark:text-gray-200"
          >
            ×
          </button>
        </div>
        <div className="mt-4">{children}</div>
      </div>
    </div>
  );
};

const EditCompetitionSettingsModal = ({ isOpen, onClose, competitionId }) => {
  const [settings, setSettings] = useState(null);

  const categoryLabels = {
    baby: "Éveils / Baby / U7",
    minipoussin: "Mini-Poussin(e)s / U9",
    poussin: "Poussin(e)s / U11",
    benjamin: "Benjamin(e)s / U13",
    minime: "Minimes / U15",
    cadet: "Cadet(te)s / U18",
    junior: "Juniors / U21",
    senior: "Seniors-Vétérans",
  };

  useEffect(() => {
    const fetchSettings = async () => {
      if (!isOpen || !competitionId) return;
      try {
        const data = await getCompetitionSettingsByCompetitionId(competitionId);
        if (Array.isArray(data) && data.length > 0) setSettings(data[0]);
      } catch (error) {
        console.error("Erreur chargement des settings:", error);
        toast.error("Impossible de charger les paramètres.");
      }
    };
    fetchSettings();
  }, [isOpen, competitionId]);

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    setSettings((prev) => ({
      ...prev,
      [name]:
        type === "checkbox"
          ? checked
          : isNaN(value)
          ? value
          : parseInt(value, 10),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await updateCompetitionSetting(settings.id, settings);
      toast.success("Paramètres mis à jour.");
      onClose?.();
    } catch (error) {
      console.error("Erreur mise à jour des settings:", error);
      toast.error("Erreur lors de la mise à jour.");
    }
  };

  const formatSeconds = (val) => {
    const n = Number(val);
    if (!Number.isFinite(n) || n < 0) return "—";
    const m = Math.floor(n / 60);
    const s = n % 60;
    return `${m}min:${String(s).padStart(2, "0")}sec`;
  };

  if (!isOpen || !settings) return null;

  return (
    <SlideOver
      open={isOpen}
      onClose={onClose}
      title="Paramètres de la compétition"
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Durées par catégorie */}
        <section>
          <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">
            Durée des combats (secondes)
          </h4>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {Object.keys(categoryLabels).map((cat) => (
              <label key={cat} className="block text-sm">
                <span className="mb-1 block text-gray-700 dark:text-gray-300">
                  {categoryLabels[cat]}
                </span>

                <div className="relative">
                  <input
                    type="number"
                    name={`temps_${cat}`}
                    value={settings[`temps_${cat}`] ?? ""}
                    onChange={handleChange}
                    min={0}
                    step={1}
                    className="w-full rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 pr-28 text-sm dark:text-white"
                  />
                  {/* conversion minutes:secondes à droite */}
                  <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-600 dark:text-gray-300">
                    {formatSeconds(settings[`temps_${cat}`])}
                  </span>
                </div>
              </label>
            ))}
          </div>
        </section>

        {/* Règles */}
        <section>
          <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">
            Règles d’arbitrage
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                name="wazari_awasete_ippon"
                checked={!!settings.wazari_awasete_ippon}
                onChange={handleChange}
              />
              <span>Waza-ari Awasete Ippon</span>
            </label>
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                name="ippon_met_fin"
                checked={!!settings.ippon_met_fin}
                onChange={handleChange}
              />
              <span>Ippon met fin au combat</span>
            </label>
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                name="deux_ippon_fin"
                checked={!!settings.deux_ippon_fin}
                onChange={handleChange}
              />
              <span>2 Ippons mettent fin</span>
            </label>
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                name="points_met_fin"
                checked={!!settings.points_met_fin}
                onChange={handleChange}
              />
              <span>Fin au nombre de points</span>
            </label>
          </div>

          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block text-sm">
              <span className="mb-1 block text-gray-700 dark:text-gray-300">
                Points fin
              </span>
              <input
                type="number"
                name="points_fin"
                value={settings.points_fin ?? 0}
                onChange={handleChange}
                className="w-full rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm dark:text-white"
                min={0}
              />
            </label>

            <label className="block text-sm">
              <span className="mb-1 block text-gray-700 dark:text-gray-300">
                Pays de la compétition
              </span>
              <select
                name="cate_country"
                value={settings.cate_country || "France"}
                onChange={handleChange}
                className="w-full rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm dark:text-white"
              >
                <option value="France">France</option>
                <option value="Belgique">Belgique</option>
              </select>
            </label>
          </div>

          {/* 🆕 Quota de compétiteurs */}
          <div className="mt-4">
            <label className="block text-sm">
              <span className="mb-1 block text-gray-700 dark:text-gray-300">
                Quota maximum de compétiteurs
              </span>
              <input
                type="number"
                name="quota_competition"
                min={0}
                value={settings.quota_competition ?? ""}
                onChange={handleChange}
                className="w-full rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm dark:text-white"
                placeholder="Ex : 200"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Les compétiteurs inscrits au-delà de ce quota seront
                automatiquement considérés comme étant sur liste d’attente.
              </p>
            </label>
          </div>
        </section>

        <div className="pt-2 flex items-center justify-end gap-2">
          <Button
            variant="outline"
            icon={FaTimes}
            onClick={onClose}
            className="rounded-lg"
          >
            Fermer
          </Button>
          <Button type="submit" icon={FaSave} className="rounded-lg">
            Mettre à jour
          </Button>
        </div>
      </form>
    </SlideOver>
  );
};

export default EditCompetitionSettingsModal;
